import React, { useState, useRef, useEffect } from 'react';

/**
 * Tooltip component that uses position: fixed to avoid being clipped by overflow containers
 */
const Tooltip = ({ children, text, variant = 'default' }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const triggerRef = useRef(null);
  const tooltipRef = useRef(null);

  const updatePosition = () => {
    if (!triggerRef.current) return;
    
    const rect = triggerRef.current.getBoundingClientRect();
    const tooltipHeight = tooltipRef.current?.offsetHeight || 80;
    const tooltipWidth = tooltipRef.current?.offsetWidth || 280;
    
    // Position above the element, centered
    let top = rect.top - tooltipHeight - 10;
    let left = rect.left + (rect.width / 2) - (tooltipWidth / 2);
    
    // Keep tooltip within viewport
    if (left < 10) left = 10;
    if (left + tooltipWidth > window.innerWidth - 10) {
      left = window.innerWidth - tooltipWidth - 10;
    }
    if (top < 10) {
      // Show below if not enough space above
      top = rect.bottom + 10;
    }
    
    setPosition({ top, left });
  };

  useEffect(() => {
    if (isVisible) {
      updatePosition();
      window.addEventListener('scroll', updatePosition, true);
      window.addEventListener('resize', updatePosition);
    }
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [isVisible]);

  const getBorderColor = () => {
    switch (variant) {
      case 'gold': return '#EAB308';
      case 'gray': return '#6B7280';
      default: return '#374151';
    }
  };

  const getBackground = () => {
    switch (variant) {
      case 'gold': return 'linear-gradient(135deg, #1F2937 0%, #374151 100%)';
      default: return '#1F2937';
    }
  };

  return (
    <>
      <span
        ref={triggerRef}
        onMouseEnter={() => setIsVisible(true)}
        onMouseLeave={() => setIsVisible(false)}
        className="cursor-help inline-flex"
      >
        {children}
      </span>
      
      {isVisible && (
        <div
          ref={tooltipRef}
          style={{
            position: 'fixed',
            top: position.top,
            left: position.left,
            zIndex: 999999,
            padding: '10px 14px',
            background: getBackground(),
            color: 'white',
            fontSize: '13px',
            lineHeight: '1.5',
            borderRadius: '8px',
            whiteSpace: 'pre-line',
            minWidth: '220px',
            maxWidth: '320px',
            textAlign: 'left',
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.5)',
            border: `1px solid ${getBorderColor()}`,
            pointerEvents: 'none',
          }}
        >
          {text}
        </div>
      )}
    </>
  );
};

export default Tooltip;
